import { FormEvent, useEffect, useState } from 'react';
import { Button, CircularProgress, IconButton, InputAdornment, TextField } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import ROUTES from '../../common/Routing/Routes';
import { useAuth } from '../../common/Auth/AuthProvider';
import { ApiError } from '../../common/Rest/ReaderlyHttpClient';
import { acceptAccountInvite, validateAccountInvite } from '../../common/Rest/Services/AccountInviteService';
import { ValidateConfirmPassword, ValidateEmail, ValidatePassword, ValidatePhone } from '../../common/Utils/Utils';
import '../PasswordRecoveryPage/PasswordRecoveryPage.css';

type ClaimStep = 'loading' | 'invalid' | 'form';

function ClaimAccountPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login, logout, isAuthenticated } = useAuth();
  const token = (searchParams.get('code') || '').trim();

  const [step, setStep] = useState<ClaimStep>(token ? 'loading' : 'invalid');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    validateAccountInvite(token)
      .then((result) => {
        if (cancelled) return;
        if (!result.isValid) {
          setStep('invalid');
          return;
        }

        setName(result.name || '');
        setEmail(result.email || '');
        setPhone(result.phoneNumber || '');
        setStep('form');
      })
      .catch(() => {
        if (!cancelled) setStep('invalid');
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const nextErrors: { [key: string]: string } = {};
    const emailValidation = ValidateEmail(email);
    const passwordValidation = ValidatePassword(newPassword);
    const confirmValidation = ValidateConfirmPassword(newPassword, confirmPassword);

    if (!emailValidation.isValid) nextErrors.email = emailValidation.validationMessage || '';
    if (phone.trim()) {
      const phoneValidation = ValidatePhone(phone);
      if (!phoneValidation.isValid) nextErrors.phone = phoneValidation.validationMessage || '';
    }
    if (!passwordValidation.isValid) nextErrors.newPassword = passwordValidation.validationMessage || '';
    if (!confirmValidation.isValid) nextErrors.confirmPassword = confirmValidation.validationMessage || '';

    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    try {
      setSubmitting(true);
      setError(null);
      const result = await acceptAccountInvite({
        token,
        email: email.trim(),
        phoneNumber: phone.trim() || undefined,
        newPassword,
      });

      if (isAuthenticated) logout();
      login(result.token);
      navigate(ROUTES.PROFILE, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setFieldErrors({ email: 'Ця пошта вже використовується іншим акаунтом.' });
      } else if (err instanceof ApiError && err.status === 401) {
        setStep('invalid');
      } else {
        setError('Не вдалося активувати акаунт. Спробуйте ще раз.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const passwordAdornment = {
    input: {
      endAdornment: (
        <InputAdornment position="end">
          <IconButton
            aria-label={showPassword ? 'Приховати пароль' : 'Показати пароль'}
            onClick={() => setShowPassword((prev) => !prev)}
            edge="end"
            tabIndex={-1}
          >
            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
          </IconButton>
        </InputAdornment>
      ),
    },
  };

  return (
    <div className="auth-page default-container">
      <div className="password-recovery-page">
        <div className="password-recovery-heading">
          <h1>Активація акаунта</h1>
          <p>
            {name ? `${name}, ми` : 'Ми'} створили для вас акаунт у Readerly. Вкажіть свою пошту, телефон і пароль —
            і акаунт разом з історією замовлень стане вашим.
          </p>
        </div>

        {step === 'loading' && <CircularProgress sx={{ alignSelf: 'center' }} />}

        {step === 'invalid' && (
          <>
            <p className="password-recovery-message">
              Посилання недійсне, вже використане або строк його дії минув. Напишіть нам — ми надішлемо нове.
            </p>
            <div className="password-recovery-actions">
              <NavLink to={ROUTES.LOGIN}>Перейти до входу</NavLink>
            </div>
          </>
        )}

        {step === 'form' && (
          <form className="login-page" onSubmit={handleSubmit} noValidate>
            <TextField
              id="claim-email"
              label="Email"
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              error={!!fieldErrors.email}
              helperText={fieldErrors.email || 'Через цю пошту ви будете входити на сайт'}
              fullWidth
            />
            <TextField
              id="claim-phone"
              label="Телефон"
              type="tel"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              error={!!fieldErrors.phone}
              helperText={fieldErrors.phone}
              fullWidth
            />
            <TextField
              id="claim-new-password"
              label="Пароль"
              type={showPassword ? 'text' : 'password'}
              required
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              error={!!fieldErrors.newPassword}
              helperText={fieldErrors.newPassword}
              fullWidth
              slotProps={passwordAdornment}
            />
            <TextField
              id="claim-confirm-password"
              label="Повторіть пароль"
              type={showPassword ? 'text' : 'password'}
              required
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              error={!!fieldErrors.confirmPassword}
              helperText={fieldErrors.confirmPassword}
              fullWidth
            />

            {error && <div className="error-message">{error}</div>}

            <Button variant="contained" type="submit" disabled={submitting} fullWidth>
              {submitting ? 'Активуємо...' : 'Активувати акаунт'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}

export default ClaimAccountPage;
